import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { User, Save, AlertCircle, QrCode, Upload, Trash2, Shield, ArrowLeft, Bell, CheckCircle, XCircle, AlertTriangle, Eye, Image as ImageIcon, MessageCircle } from 'lucide-react';
import ProfileGigsSection from '@/features/artist-mode/components/ProfileGigsSection';
import SupportThreadView from '@/features/artist-mode/components/SupportThreadView';
import QRCode from 'qrcode.react';

// ✅ IMPORT THE NEW FIREBASE AUTH CONTEXT AND SERVICES
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { COUNTRY_CURRENCY_MAP, getCurrencyForCountry } from '@/lib/currency';
import { COUNTRIES } from '@/lib/countries';
import { db, storage, functions } from '@/lib/firebase';
import { doc, updateDoc, getDocs, collection, query, where, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth, updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { httpsCallable } from 'firebase/functions';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import MobileProfile from '@/features/shared/components/MobileProfile';
import ImageCropper from '@/components/ImageCropper';
import { isQrPromoReservedSlug, QR_SLUG_RESERVED_MESSAGE } from '@/lib/reservedUrlSlug';

const ProfilePage = () => {
    // ✅ USEAUTH NOW PULLS FROM FIREBASE
    const { user, profile, updateProfileData, refreshProfile } = useAuth();
    const { currency, country, updateCurrency } = useCurrency();
    const { toast } = useToast();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const mobile = useMobile();

    const profileMobileDefaultTab =
        searchParams.get('tab') === 'alerts' ? 'notifications' : 'profile';

    useLayoutEffect(() => {
        if (searchParams.get('tab') !== 'alerts') return;
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('tab');
                return next;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '', artist_name: '', bio: '', tagline: '', profile_picture_url: '', header_image_url: '', url_slug: '',
        social_facebook: '', social_x: '', social_instagram: '', social_tiktok: ''
    });

    const [securityData, setSecurityData] = useState({
        newEmail: '', currentPassword: '', newPassword: '', confirmPassword: '', deleteConfirmation: ''
    });
    const [updatingEmail, setUpdatingEmail] = useState(false);
    const [updatingPassword, setUpdatingPassword] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [checkingSlug, setCheckingSlug] = useState(false);
    
    // Header image states
    const [headerImageFile, setHeaderImageFile] = useState(null);
    const [headerImagePreview, setHeaderImagePreview] = useState(null);
    const [showHeaderCropDialog, setShowHeaderCropDialog] = useState(false);
    const [uploadingHeaderImage, setUploadingHeaderImage] = useState(false);
    
    // Push notifications removed - service workers disabled
    
    useEffect(() => {
        if (profile) {
            setFormData({
                full_name: profile.full_name || '',
                artist_name: profile.artist_name || '',
                bio: profile.bio || '',
                tagline: profile.tagline || '',
                profile_picture_url: profile.profile_picture_url || '',
                header_image_url: profile.header_image_url || '',
                url_slug: profile.url_slug || '',
                social_facebook: profile.social_facebook || '',
                social_x: profile.social_x || '',
                social_instagram: profile.social_instagram || '',
                social_tiktok: profile.social_tiktok || '',
            });
        }
    }, [profile]);

    // Push notification functions removed - service workers disabled

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCountryChange = async (newCountry) => {
        if (!user) return;
        const { getCurrencyForCountry } = await import('@/lib/currency');
        const newCurrency = getCurrencyForCountry(newCountry);
        try {
            const profileDocRef = doc(db, 'profiles', user.uid);
            await updateDoc(profileDocRef, {
                country: newCountry,
                currency: newCurrency
            });
            updateCurrency(newCurrency, newCountry);
            if (refreshProfile) await refreshProfile();
            toast({
                title: "Country & Currency Updated",
                description: `Country changed to ${COUNTRIES.find(c => c.code === newCountry)?.name || newCountry}. Currency is now ${newCurrency}.`,
                variant: "default"
            });
        } catch (error) {
            console.error("Country update error:", error);
            toast({
                title: "Error",
                description: "Could not update country. Please try again.",
                variant: "destructive"
            });
        }
    };
    
    const handleSlugChange = (e) => {
        // Prevent changes if Profile URL already exists
        if (profile?.url_slug) {
            return;
        }
        
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        setFormData(prev => ({...prev, url_slug: value}));
    }

    // ✅ REFACTORED: Image upload uses Firebase Storage
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !user) return;

        setUploadingImage(true);
        const filePath = `avatars/${user.uid}/${file.name}`;
        const storageRef = ref(storage, filePath);

        try {
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            const profileDocRef = doc(db, 'profiles', user.uid);
            await updateDoc(profileDocRef, { profile_picture_url: downloadURL });
            
            // This assumes your useAuth context will automatically refresh the profile.
            // If not, you might need to manually update the local state.
            setFormData(prev => ({ ...prev, profile_picture_url: downloadURL }));
            
            toast({ title: "Success!", description: "Profile picture updated." });
        } catch (error) {
            console.error("Firebase Storage Error:", error);
            toast({ title: "Upload Failed", description: "Could not upload image.", variant: "destructive" });
        } finally {
            setUploadingImage(false);
        }
    };
    
    // ✅ REFACTORED: Remove image uses Firebase Storage
    const handleRemoveImage = async () => {
        if (!user || !profile?.profile_picture_url) return;

        try {
            // Extract the storage path from the full URL
            // Firebase Storage URLs are like: https://firebasestorage.googleapis.com/v0/b/.../o/avatars%2F...
            // We need to decode and extract the path
            let storagePath = profile.profile_picture_url;
            
            // Try to extract path from Firebase Storage URL
            try {
                const url = new URL(profile.profile_picture_url);
                const pathMatch = url.pathname.match(/\/o\/(.+)/);
                if (pathMatch) {
                    storagePath = decodeURIComponent(pathMatch[1]);
                }
            } catch (e) {
                // If URL parsing fails, assume it's already a path
                console.log('Using profile_picture_url as-is for deletion');
            }
            
            // Delete the file from Firebase Storage
            const fileRef = ref(storage, storagePath);
            await deleteObject(fileRef);
            
            // Then update the profile document
            const profileDocRef = doc(db, 'profiles', user.uid);
            await updateDoc(profileDocRef, { profile_picture_url: '' });
            
            setFormData(prev => ({ ...prev, profile_picture_url: '' }));
            if (updateProfileData) updateProfileData();
            
            toast({ title: "Success!", description: "Profile picture removed." });
        } catch (error) {
            console.error("Remove image error:", error);
            // Even if storage deletion fails, still remove from profile
            try {
                const profileDocRef = doc(db, 'profiles', user.uid);
                await updateDoc(profileDocRef, { profile_picture_url: '' });
                setFormData(prev => ({ ...prev, profile_picture_url: '' }));
                if (updateProfileData) updateProfileData();
                toast({ title: "Success!", description: "Profile picture removed from profile." });
            } catch (updateError) {
                toast({ title: "Error", description: "Could not remove image.", variant: "destructive" });
            }
        }
    };
    
    // ✅ Header Image Upload Handler
    const handleHeaderImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast({ title: "Invalid file", description: "Please select an image file.", variant: "destructive" });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast({ title: "File too large", description: "File must be less than 10MB.", variant: "destructive" });
            return;
        }
        setHeaderImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
            setHeaderImagePreview(e.target.result);
            setShowHeaderCropDialog(true);
        };
        reader.readAsDataURL(file);
    };

    // ✅ Header Image Crop Save Handler
    const handleHeaderImageSave = async (blob) => {
        if (!user) {
            console.error('Header image save: No user found');
            toast({ title: "Error", description: "You must be logged in to upload images.", variant: "destructive" });
            return;
        }
        
        if (!blob) {
            console.error('Header image save: No blob provided');
            toast({ title: "Error", description: "No image data to upload.", variant: "destructive" });
            return;
        }

        setUploadingHeaderImage(true);

        try {
            console.log('📤 Starting header image upload...');
            const fileName = `header-${user.uid}-${Date.now()}.jpg`;
            const storageRef = ref(storage, `headers/${fileName}`);
            
            console.log('📤 Uploading to Firebase Storage...');
            await uploadBytes(storageRef, blob);
            console.log('✅ Upload complete, getting download URL...');
            
            const publicUrl = await getDownloadURL(storageRef);
            console.log('✅ Download URL obtained:', publicUrl);

            const profileDocRef = doc(db, 'profiles', user.uid);
            console.log('📝 Updating Firestore profile...');
            await updateDoc(profileDocRef, { header_image_url: publicUrl });
            console.log('✅ Firestore updated');
            
            setFormData(prev => ({ ...prev, header_image_url: publicUrl }));
            
            // Refresh profile data to get updated header image
            if (refreshProfile) {
                try {
                    console.log('🔄 Refreshing profile data...');
                    await refreshProfile();
                } catch (refreshError) {
                    console.warn('⚠️ Error refreshing profile (non-critical):', refreshError);
                    // Don't fail the upload if refresh fails
                }
            }
            
            toast({ title: "Success!", description: "Header image uploaded successfully!" });
            setShowHeaderCropDialog(false);
            setHeaderImagePreview(null);
            setHeaderImageFile(null);
        } catch (error) {
            console.error('❌ Header image upload error:', error);
            console.error('❌ Error details:', {
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            toast({ 
                title: "Error", 
                description: error.message || "Failed to upload header image. Please try again.", 
                variant: "destructive" 
            });
        } finally {
            setUploadingHeaderImage(false);
        }
    };

    // ✅ Header Image Remove Handler
    const handleRemoveHeaderImage = async () => {
        if (!user || !profile?.header_image_url) return;

        try {
            const fileRef = ref(storage, profile.header_image_url);
            await deleteObject(fileRef);
            
            const profileDocRef = doc(db, 'profiles', user.uid);
            await updateDoc(profileDocRef, { header_image_url: '' });
            
            setFormData(prev => ({ ...prev, header_image_url: '' }));
            if (updateProfileData) updateProfileData();
            
            toast({ title: "Success!", description: "Header image removed." });
        } catch (error) {
            console.error("Remove header image error:", error);
            toast({ title: "Error", description: "Could not remove header image.", variant: "destructive" });
        }
    };

    // ✅ Preview Public Page Handler
    const handlePreviewPublicPage = () => {
        if (!profile?.url_slug) {
            toast({ 
                title: "Profile URL Required", 
                description: "Please set your Profile URL first to preview your public page.", 
                variant: "destructive" 
            });
            return;
        }
        const accountType = profile?.account_type || 'artist';
        const previewUrl = accountType === 'karaoke' 
            ? `/karaoke/${profile.url_slug}` 
            : `/artist/${profile.url_slug}`;
        window.open(previewUrl, '_blank');
    };

    // ✅ REFACTORED: Check if Profile URL is available
    const checkSlugAvailability = async (slug) => {
        if (!slug) return true;
        
        setCheckingSlug(true);
        try {
            const profilesRef = collection(db, 'profiles');
            const q = query(profilesRef, where('url_slug', '==', slug));
            const querySnapshot = await getDocs(q);
            
            // If no other profiles use this slug, it's available
            if (querySnapshot.empty) {
                return true;
            }
            
            // If this slug is used by the current user (updating their own profile), it's available
            if (querySnapshot.docs[0].id === user?.uid) {
                return true;
            }
            
            // Slug is taken by another user
            return false;
        } catch (error) {
            console.error('Error checking slug availability:', error);
            
            // If it's a permissions error, we'll assume the slug is available
            // This prevents the app from breaking due to security rules
            if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
                console.warn('Firestore permissions issue - assuming slug is available');
                return true;
            }
            
            // For other errors, we'll be conservative and assume it's not available
            return false;
        } finally {
            setCheckingSlug(false);
        }
    };

    // ✅ REFACTORED: Form submission uses Firebase
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;

        setLoading(true);
        try {
            // Prevent Full Name changes if payout account is connected
            if (profile?.stripe_account_id && formData.full_name !== profile.full_name) {
                toast({ 
                    title: "Cannot Change Full Name",
                    description: "Full Name cannot be changed once your payment account is connected. Contact support if you need assistance.", 
                    variant: "destructive" 
                });
                setLoading(false);
                return;
            }

            // Prevent Profile URL changes if one already exists
            if (profile?.url_slug && formData.url_slug !== profile.url_slug) {
                toast({ 
                                    title: "Cannot Change Profile URL",
                description: "Your Profile URL cannot be changed once set. Please contact support if you need assistance.", 
                    variant: "destructive" 
                });
                setLoading(false);
                return;
            }

            if (formData.url_slug && isQrPromoReservedSlug(formData.url_slug)) {
                toast({
                    title: 'Invalid profile URL',
                    description: QR_SLUG_RESERVED_MESSAGE,
                    variant: 'destructive',
                });
                setLoading(false);
                return;
            }

            // Only check slug availability if the slug has changed and no slug exists yet
            if (formData.url_slug && !profile?.url_slug) {
                const isAvailable = await checkSlugAvailability(formData.url_slug);
                if (!isAvailable) {
                    toast({ title: "Error", description: "This Profile URL is already taken. Please choose another.", variant: "destructive" });
                    setLoading(false);
                    return;
                }
            }

            // Update profile in Firestore
            await updateProfileData(user.uid, formData);
            
            toast({ title: "Success!", description: "Profile updated successfully!" });
        } catch (error) {
            console.error("Profile update error:", error);
            
            // Handle specific Firestore errors
            if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
                toast({ 
                    title: "Permission Error", 
                    description: "Unable to update profile due to database permissions. Please contact support.", 
                    variant: "destructive" 
                });
            } else {
                toast({ title: "Error", description: "Could not update profile.", variant: "destructive" });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSecurityInputChange = (e) => {
        const { name, value } = e.target;
        setSecurityData(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateEmail = async () => {
        const { newEmail, currentPassword } = securityData;
        if (!newEmail || !currentPassword) {
            toast({ title: "Error", description: "Please provide a new email and your current password.", variant: "destructive" });
            return;
        }

        setUpdatingEmail(true);
        const auth = getAuth();
        const currentUser = auth.currentUser;

        try {
            const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);
            await updateEmail(currentUser, newEmail);
            toast({ title: "Success!", description: "Email update initiated. Check your new email for verification." });
            setSecurityData(prev => ({ ...prev, newEmail: '', currentPassword: '' }));
        } catch (error) {
            console.error("Email update error:", error);
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setUpdatingEmail(false);
        }
    };

    const handleUpdatePassword = async (currentPasswordParam, newPasswordParam, confirmPasswordParam) => {
        // Support both parameter-based calls (from mobile) and state-based calls (from desktop)
        const currentPassword = currentPasswordParam || securityData.currentPassword;
        const newPassword = newPasswordParam || securityData.newPassword;
        const confirmPassword = confirmPasswordParam || securityData.confirmPassword;
        
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast({ title: "Error", description: "Please fill all password fields.", variant: "destructive" });
            return;
        }
        if (newPassword !== confirmPassword) {
            toast({ title: "Error", description: "New passwords do not match.", variant: "destructive" });
            return;
        }

        setUpdatingPassword(true);
        const auth = getAuth();
        const currentUser = auth.currentUser;

        try {
            const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
            await reauthenticateWithCredential(currentUser, credential);
            await updatePassword(currentUser, newPassword);
            toast({ title: "Success!", description: "Password updated successfully!" });
            setSecurityData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
        } catch (error) {
            console.error("Password update error:", error);
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setUpdatingPassword(false);
        }
    };

    const handleDeleteAccount = async () => {
        if (!user) return;

        try {
            // Get the user's ID token for authentication
            const idToken = await user.getIdToken();
            
            // Use HTTP Cloud Function for complete account deletion (with CORS support)
            const response = await fetch('https://us-central1-yoursongrequest-fe6e9.cloudfunctions.net/deleteOwnAccountHttp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete account');
            }

            const result = await response.json();

            // Delete user's profile picture from storage if it exists
            if (profile?.profile_picture_url) {
                try {
                    const imageRef = ref(storage, `avatars/${user.uid}/`);
                    await deleteObject(imageRef);
                } catch (error) {
                    console.error("Error deleting profile picture:", error);
                    // Don't fail the entire operation for storage cleanup
                }
            }

            toast({ 
                title: "✅ Account Deleted", 
                description: "Your account and all data have been permanently deleted. You can now sign up again with the same email address." 
            });
            
            // Navigate to home page
            navigate('/');
        } catch (error) {
            console.error("Delete account error:", error);
            toast({ 
                title: "❌ Deletion Failed", 
                description: error.message || "Could not delete account. Please try again.", 
                variant: "destructive" 
            });
        }
    };

    return (
        <>
            <Helmet><title>Profile Management - Your Song Request</title></Helmet>
            
            {/* Mobile-optimized Profile */}
            {mobile.isMobile && (
                <MobileProfile
                    authUserId={user?.uid || null}
                    profile={profile}
                    formData={formData}
                    onInputChange={handleInputChange}
                    onSave={handleFormSubmit}
                    onImageUpload={handleImageUpload}
                    onRemoveImage={handleRemoveImage}
                    uploadingImage={uploadingImage}
                    loading={loading}
                    onUpdateEmail={handleUpdateEmail}
                    onUpdatePassword={handleUpdatePassword}
                    onDeleteAccount={handleDeleteAccount}
                    country={country || profile?.country || 'GB'}
                    currency={currency}
                    onCountryChange={handleCountryChange}
                    defaultTab={profileMobileDefaultTab}
                />
            )}
            
            {/* Desktop Profile */}
            {!mobile.isMobile && (
                <main className="w-full h-full p-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        {/* Back Button */}


                    <Card className="glass-effect border-gray-800">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center text-2xl">
                                        <User className="w-6 h-6 mr-3 text-blue-500" />
                                        Profile Management
                                    </CardTitle>
                                    <CardDescription>
                                        Manage your artist profile, Profile URL, and social media links
                                    </CardDescription>
                                </div>
                                {profile?.url_slug && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handlePreviewPublicPage}
                                        className="flex items-center space-x-2"
                                    >
                                        <Eye className="w-4 h-4" />
                                        <span>Preview Public Page</span>
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleFormSubmit} className="space-y-6">
                                {/* Full Name */}
                                <div className="space-y-2">
                                    <Label htmlFor="full_name">Full Name {profile?.stripe_account_id && <span className="text-xs text-gray-400">(Locked)</span>}</Label>
                                    <Input
                                        id="full_name"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        placeholder="Enter your full name"
                                        readOnly={!!profile?.stripe_account_id}
                                        className={`bg-gray-800 border-gray-600 text-white ${profile?.stripe_account_id ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    />
                                    {profile?.stripe_account_id && (
                                        <p className="text-xs text-amber-200">
                                            Full Name cannot be changed once your payment account is connected. Contact support if you need to update it.
                                        </p>
                                    )}
                                </div>

                                {/* Artist Name */}
                                <div className="space-y-2">
                                    <Label htmlFor="artist_name">Artist Name *</Label>
                                    <Input
                                        id="artist_name"
                                        name="artist_name"
                                        value={formData.artist_name}
                                        onChange={handleInputChange}
                                        placeholder="Enter your artist name"
                                        required
                                        className="bg-gray-800 border-gray-600 text-white"
                                    />
                                </div>

                                {/* Tagline */}
                                <div className="space-y-2">
                                    <Label htmlFor="tagline">Tagline</Label>
                                    <Input
                                        id="tagline"
                                        name="tagline"
                                        value={formData.tagline}
                                        onChange={handleInputChange}
                                        placeholder="A short, catchy tagline that describes you..."
                                        maxLength={100}
                                        className="bg-gray-800 border-gray-600 text-white"
                                    />
                                    <p className="text-sm text-gray-400">
                                        A brief tagline that appears on your public profile when offline (max 100 characters)
                                    </p>
                                </div>

                                                            {/* Profile URL */}
                            <div className="space-y-2">
                                <Label htmlFor="url_slug">Profile URL *</Label>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-gray-400">yoursongrequest.com/</span>
                                        <Input
                                            id="url_slug"
                                            name="url_slug"
                                            value={formData.url_slug}
                                            onChange={handleSlugChange}
                                            placeholder="your-artist-name"
                                            required
                                            readOnly={!!profile?.url_slug}
                                            className={`bg-gray-800 border-gray-600 text-white ${profile?.url_slug ? 'opacity-60 cursor-not-allowed' : ''}`}
                                        />
                                    </div>
                                    {profile?.url_slug ? (
                                        <div className="flex items-center space-x-2 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                                            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                                            <p className="text-sm text-amber-200">
                                                <strong>Profile URL cannot be changed.</strong> Your public page URL is permanently set to: <code className="bg-amber-900/50 px-1 rounded">yoursongrequest.com/{profile.url_slug}</code>
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400">
                                            This will be your public page URL. Use only letters, numbers, and hyphens. <strong>This cannot be changed once set.</strong>
                                        </p>
                                    )}
                                </div>

                                {/* Bio */}
                                <div className="space-y-2">
                                    <Label htmlFor="bio">Bio</Label>
                                    <Textarea
                                        id="bio"
                                        name="bio"
                                        value={formData.bio}
                                        onChange={handleInputChange}
                                        placeholder="Tell fans about yourself and your music..."
                                        rows={4}
                                        className="bg-gray-800 border-gray-600 text-white"
                                    />
                                </div>

                                {/* Header Image */}
                                <div className="space-y-4">
                                    <Label>Header Image</Label>
                                    <p className="text-sm text-gray-400 mb-2">
                                        A wide banner image displayed at the top of your public profile (recommended: 1920x600px)
                                    </p>
                                    <div className="space-y-3">
                                        {formData.header_image_url ? (
                                            <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-gray-600 bg-gray-800">
                                                <img
                                                    src={formData.header_image_url}
                                                    alt="Header"
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute top-2 right-2 flex space-x-2">
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => {
                                                            const input = document.createElement('input');
                                                            input.type = 'file';
                                                            input.accept = 'image/*';
                                                            input.onchange = handleHeaderImageUpload;
                                                            input.click();
                                                        }}
                                                        disabled={uploadingHeaderImage}
                                                        className="bg-gray-700 hover:bg-gray-600 text-white"
                                                    >
                                                        <ImageIcon className="w-4 h-4 mr-1" />
                                                        Change
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="destructive"
                                                        size="sm"
                                                        onClick={handleRemoveHeaderImage}
                                                        className="bg-red-600 hover:bg-red-700"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="relative w-full h-48 rounded-lg border-2 border-dashed border-gray-600 bg-gray-800/50 flex flex-col items-center justify-center">
                                                <ImageIcon className="w-12 h-12 text-gray-500 mb-2" />
                                                <p className="text-gray-400 text-sm mb-3">No header image</p>
                                                <Input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleHeaderImageUpload}
                                                    disabled={uploadingHeaderImage}
                                                    className="hidden"
                                                    id="header-image-upload"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={() => {
                                                        document.getElementById('header-image-upload')?.click();
                                                    }}
                                                    disabled={uploadingHeaderImage}
                                                    className="border-gray-600 text-white hover:bg-gray-700"
                                                >
                                                    <Upload className="w-4 h-4 mr-2" />
                                                    {uploadingHeaderImage ? 'Uploading...' : 'Upload Header Image'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Profile Picture */}
                                <div className="space-y-4">
                                    <Label>Profile Picture</Label>
                                    <div className="flex items-center space-x-4">
                                        {formData.profile_picture_url ? (
                                            <div className="relative">
                                                <img
                                                    src={formData.profile_picture_url}
                                                    alt="Profile"
                                                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-600"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="destructive"
                                                    size="sm"
                                                    onClick={handleRemoveImage}
                                                    className="absolute -top-2 -right-2 w-6 h-6 p-0 rounded-full"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="w-20 h-20 rounded-full bg-gray-700 border-2 border-dashed border-gray-500 flex items-center justify-center">
                                                <User className="w-8 h-8 text-gray-400" />
                                            </div>
                                        )}
                                        <div className="space-y-2">
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                disabled={uploadingImage}
                                                className="bg-gray-800 border-gray-600 text-white"
                                            />
                                            <p className="text-sm text-gray-400">
                                                {uploadingImage ? 'Uploading...' : 'Upload a profile picture (JPG, PNG)'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Social Media Links */}
                                <div className="space-y-4">
                                    <Label>Social Media Links</Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="social_facebook">Facebook</Label>
                                            <Input
                                                id="social_facebook"
                                                name="social_facebook"
                                                value={formData.social_facebook}
                                                onChange={handleInputChange}
                                                placeholder="https://facebook.com/yourpage"
                                                className="bg-gray-800 border-gray-600 text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="social_x">X (Twitter)</Label>
                                            <Input
                                                id="social_x"
                                                name="social_x"
                                                value={formData.social_x}
                                                onChange={handleInputChange}
                                                placeholder="https://x.com/yourhandle"
                                                className="bg-gray-800 border-gray-600 text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="social_instagram">Instagram</Label>
                                            <Input
                                                id="social_instagram"
                                                name="social_instagram"
                                                value={formData.social_instagram}
                                                onChange={handleInputChange}
                                                placeholder="https://instagram.com/yourhandle"
                                                className="bg-gray-800 border-gray-600 text-white"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="social_tiktok">TikTok</Label>
                                            <Input
                                                id="social_tiktok"
                                                name="social_tiktok"
                                                value={formData.social_tiktok}
                                                onChange={handleInputChange}
                                                placeholder="https://tiktok.com/@yourhandle"
                                                className="bg-gray-800 border-gray-600 text-white"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="flex justify-end space-x-3">
                                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                                        {loading ? 'Saving...' : 'Save Profile'}
                                    </Button>
                                </div>
                            </form>

                            {/* Currency & Localization Settings */}
                            <Card className="glass-effect border-gray-800 mt-8">
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <span className="text-2xl mr-2">💱</span>
                                        Currency & Localization
                                    </CardTitle>
                                    <CardDescription>
                                        Set your country and currency for payments and pricing
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Country Selection */}
                                    <div className="space-y-2">
                                        <Label htmlFor="country" className="text-gray-200">Country of Residence <span className="text-red-400">*</span></Label>
                                        <select
                                            id="country"
                                            value={country || 'GB'}
                                            onChange={(e) => handleCountryChange(e.target.value)}
                                            className="w-full bg-gray-800 border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        >
                                            {COUNTRIES.map((countryOption) => (
                                                <option key={countryOption.code} value={countryOption.code}>
                                                    {countryOption.label}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-400">
                                            Your country determines your currency. This is used for all payments and pricing throughout the app.
                                        </p>
                                    </div>
                                    
                                    {/* Current Currency Display */}
                                    <div className="space-y-2">
                                        <Label className="text-gray-200">Current Currency</Label>
                                        <p className="text-sm font-medium text-gray-300">
                                            {currency || 'GBP'}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Currency is automatically set based on your selected country.
                                        </p>
                                    </div>

                                    {/* Payout Account Warning */}
                                    {profile?.stripe_account_id && (
                                        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 space-y-2">
                                            <div className="flex items-start space-x-2">
                                                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-yellow-200">Payout Account Notice</p>
                                                    <p className="text-xs text-yellow-300 mt-1">
                                                        Your payout account was created with country: <strong>{profile.country || 'Unknown'}</strong>. 
                                                        We do not allow changing the country of an existing payout account. 
                                                        If you need to change your payment account country, you'll need to contact support to create a new payout account.
                                                    </p>
                                                    <p className="text-xs text-gray-400 mt-2">
                                                        Changing your country here will update your YSR account settings and currency display, but your existing payout account will remain unchanged.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <div className="mt-8 space-y-8">
                                <ProfileGigsSection />

                                <Card className="glass-effect border-gray-800">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <MessageCircle className="w-5 h-5 text-purple-400" />
                                            Support
                                        </CardTitle>
                                        <CardDescription>
                                            View replies from the YSR team and send follow-ups on your open requests.
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <SupportThreadView />
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Account Security Section */}
                            <Card className="glass-effect border-gray-800 mt-8">
                                <CardHeader>
                                    <CardTitle className="flex items-center">
                                        <Shield className="w-5 h-5 mr-2 text-blue-500" />
                                        Account Security
                                    </CardTitle>
                                    <CardDescription>
                                        Update your email address and password
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Email Update */}
                                    <div className="space-y-4">
                                        <h4 className="font-medium text-gray-200">Update Email Address</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="newEmail">New Email</Label>
                                                <Input
                                                    id="newEmail"
                                                    name="newEmail"
                                                    type="email"
                                                    value={securityData.newEmail}
                                                    onChange={handleSecurityInputChange}
                                                    placeholder="newemail@example.com"
                                                    className="bg-gray-800 border-gray-600 text-white"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="currentPassword">Current Password</Label>
                                                <Input
                                                    id="currentPassword"
                                                    name="currentPassword"
                                                    type="password"
                                                    value={securityData.currentPassword}
                                                    onChange={handleSecurityInputChange}
                                                    placeholder="Enter current password"
                                                    className="bg-gray-800 border-gray-600 text-white"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            onClick={handleUpdateEmail}
                                            disabled={updatingEmail || !securityData.newEmail || !securityData.currentPassword}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            {updatingEmail ? 'Updating...' : 'Update Email'}
                                        </Button>
                                    </div>

                                    {/* Password Update */}
                                    <div className="space-y-4 pt-6 border-t border-gray-700">
                                        <h4 className="font-medium text-gray-200">Update Password</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="currentPasswordPwd">Current Password</Label>
                                                <Input
                                                    id="currentPasswordPwd"
                                                    name="currentPassword"
                                                    type="password"
                                                    value={securityData.currentPassword}
                                                    onChange={handleSecurityInputChange}
                                                    placeholder="Enter current password"
                                                    className="bg-gray-800 border-gray-600 text-white"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="newPassword">New Password</Label>
                                                <Input
                                                    id="newPassword"
                                                    name="newPassword"
                                                    type="password"
                                                    value={securityData.newPassword}
                                                    onChange={handleSecurityInputChange}
                                                    placeholder="Enter new password"
                                                    className="bg-gray-800 border-gray-600 text-white"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                                <Input
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    type="password"
                                                    value={securityData.confirmPassword}
                                                    onChange={handleSecurityInputChange}
                                                    placeholder="Confirm new password"
                                                    className="bg-gray-800 border-gray-600 text-white"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            onClick={handleUpdatePassword}
                                            disabled={updatingPassword || !securityData.currentPassword || !securityData.newPassword || !securityData.confirmPassword}
                                            className="bg-blue-600 hover:bg-blue-700"
                                        >
                                            {updatingPassword ? 'Updating...' : 'Update Password'}
                                        </Button>
                                    </div>

                                    {/* Delete Account */}
                                    <div className="space-y-4 pt-6 border-t border-red-700">
                                        <h4 className="font-medium text-red-300">Delete Account</h4>
                                        <p className="text-gray-400 text-sm">
                                            This action cannot be undone. All your data, songs, and account information will be permanently deleted.
                                        </p>
                                        <div className="space-y-2">
                                            <Label htmlFor="deleteConfirmation">Type 'delete' to confirm</Label>
                                            <Input
                                                id="deleteConfirmation"
                                                name="deleteConfirmation"
                                                type="text"
                                                value={securityData.deleteConfirmation}
                                                onChange={handleSecurityInputChange}
                                                placeholder="Type 'delete'"
                                                className="bg-gray-800 border-gray-600 text-white"
                                            />
                                        </div>
                                        <Button
                                            onClick={handleDeleteAccount}
                                            disabled={securityData.deleteConfirmation !== 'delete'}
                                            className="bg-red-600 hover:bg-red-700 disabled:bg-red-800"
                                        >
                                            Delete Account
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                        </CardContent>
                    </Card>
                </motion.div>
            </main>
            )}
            
            {/* Header Image Cropper Dialog */}
            {showHeaderCropDialog && headerImagePreview && (
                <ImageCropper
                    imageSrc={headerImagePreview}
                    onSave={handleHeaderImageSave}
                    onCancel={() => {
                        setShowHeaderCropDialog(false);
                        setHeaderImagePreview(null);
                        setHeaderImageFile(null);
                    }}
                    aspectRatio={{ width: 16, height: 6 }} // Wide banner ratio
                    minZoom={1}
                    maxZoom={3}
                    outputWidth={1920}
                />
            )}
        </>
    );
};

export default ProfilePage;